import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, renderList as _renderList, Fragment as _Fragment, createElementBlock as _createElementBlock, isRef as _isRef, createVNode as _createVNode, normalizeClass as _normalizeClass, TransitionGroup as _TransitionGroup, withCtx as _withCtx } from "vue"

const _hoisted_1 = ["aria-hidden"]
const _hoisted_2 = {
  key: 1,
  class: "mt-x6"
}
const _hoisted_3 = { class: "sr-only" }

import useProductDetailData from '@/shared/product/details/useProductDetailData';
import useProductBasketButtonState from '@/shared/product/purchase/ui/useProductBasketButtonState';
import useProductClickAndCollect from '@/shared/product/purchase/ui/useProductClickAndCollect';
import InputRadio from '@/shared/form/InputRadio.vue';
import {
    ClickAndCollectPurchaseOption,
    PurchaseOptionType,
} from '@/types/productServerApi';
import { computed, Ref, ref, watch, WritableComputedRef } from 'vue';
import ProductDetailRequestDelivery from '@/shared/product/purchase/ui/ProductPurchaseRequestDelivery.vue';
import ProductPurchaseOptionClickAndCollectDescription from '@/shared/product/purchase/ui/ProductPurchaseOptionClickAndCollectDescription.vue';
import { useBlockLabels } from '@/shared/content/blocks/helpers/block-labels.plugin';
import useProductPurchase from '@/shared/product/purchase/ui/useProductPurchase';
import { useModuleConfigurator } from '@/shared/product/details/ui/module-configurator/useModuleConfigurator';


export default /*#__PURE__*/_defineComponent({
  __name: 'ProductPurchaseOptions',
  setup(__props) {

const labels = useBlockLabels();
const { setBasketDisabledState } = useProductBasketButtonState();
const { productData, variantId } = useProductDetailData();
const { moduleConfiguratorTileData, is3DConfiguratorFeatureEnabled } = useModuleConfigurator();

const { getUserSelectedPurchaseOptionType, setUserSelectedPurchaseOptionType } = useProductPurchase();
const { openStoreSelector, setStoreInventoryData, storeId, storeInventoryData } = useProductClickAndCollect();

const showRequestDelivery = computed(() => productData.value ? productData.value.purchasePossibilities.length === 1 && productData.value.purchasePossibilities[0].type === PurchaseOptionType.Special : false);
const showMultiplePurchaseOptions = computed(() => productData.value ? productData.value.purchasePossibilities.length > 1 : false);
const showSinglePurchaseOption = computed(() => productData.value ? productData.value.purchasePossibilities.length === 1 : false);

const singleOption = computed(() => productData.value.purchasePossibilities.length === 1 ? productData.value.purchasePossibilities[0] : false);
const standardOption = computed(() => productData.value.purchasePossibilities.find((option) => option.type === PurchaseOptionType.Standard));
const instantOption = computed(() => productData.value.purchasePossibilities.find((option) => option.type === PurchaseOptionType.Instant));
const clickAndCollectOption = computed(() => (productData.value.purchasePossibilities.find((option) => option.type === PurchaseOptionType.ClickAndCollect) as ClickAndCollectPurchaseOption));

const loadingStoreDataForNewSku: Ref<boolean> = ref(false);

const handleNewStoreData = async() => {
    if (clickAndCollectOption.value && variantId.value) {
        loadingStoreDataForNewSku.value = true;
        await setStoreInventoryData(variantId.value);
        handleInitialBasketButtonState();
        loadingStoreDataForNewSku.value = false;
    }
};

watch(variantId, handleNewStoreData, { immediate: true });

const hidePurchaseOptions = computed(() => {
    // @ts-ignore
    return showSinglePurchaseOption.value && !labels[singleOption.value.title] && !labels[singleOption.value.description] && singleOption.value.type !== PurchaseOptionType.Special;
});

const selectedPurchaseOptionType: WritableComputedRef<PurchaseOptionType> = computed({
    get: () => {
        if (loadingStoreDataForNewSku.value) {
            return getUserSelectedPurchaseOptionType.value;
        }
        return getDefaultSelectedPurchaseOption();
    },
    set(optionType: PurchaseOptionType) {
        setUserSelectedPurchaseOptionType(optionType);
    },
});

const getDefaultSelectedPurchaseOption = (): PurchaseOptionType => {
    if (is3DConfiguratorFeatureEnabled.value && moduleConfiguratorTileData.value?.modules) {
        return PurchaseOptionType.ModuleConfiguration;
    }

    if (singleOption.value) {
        return singleOption.value.type;
    }

    if (previousPurchaseOptionIsStillAvailable()) {
        return getUserSelectedPurchaseOptionType.value;
    }

    if (instantOption.value) {
        return instantOption.value.type;
    }

    if (clickAndCollectOption.value && storeId.value && !clickAndCollectIsDisabled.value) {
        return clickAndCollectOption.value.type;
    }

    if (standardOption.value) {
        return standardOption.value.type;
    }

    return productData.value.purchasePossibilities[0].type;
};

const handleBasketButtonState = (optionType: PurchaseOptionType): void => {
    const basketButtonIsDisabled = isClickAndCollectAndIsDisabled(optionType);
    setBasketDisabledState(basketButtonIsDisabled);
};

const handleInitialBasketButtonState = (): void => {
    const basketButtonIsDisabled = isClickAndCollectAndIsDisabled(getDefaultSelectedPurchaseOption());
    setBasketDisabledState(basketButtonIsDisabled);
};

const previousPurchaseOptionIsStillAvailable = (): boolean => {
    if (getUserSelectedPurchaseOptionType.value !== null) {
        const previouslySelectedOption = productData.value.purchasePossibilities.find((option) => option.type === getUserSelectedPurchaseOptionType.value);
        return !!previouslySelectedOption && !isClickAndCollectAndIsDisabled(previouslySelectedOption.type);
    }
    return false;
};

const isDisabled = (purchaseOptionType: PurchaseOptionType): boolean | null => {
    return (showSinglePurchaseOption.value || isClickAndCollectAndIsDisabled(purchaseOptionType)) ? true : null;
};

const onPurchaseOptionChange = () => {
    handleClickAndCollectSelection(selectedPurchaseOptionType.value);
    handleBasketButtonState(selectedPurchaseOptionType.value);
};

const handleClickAndCollectSelection = (optionType: PurchaseOptionType): void => {
    const isClickAndCollectWithoutSelectedStore = optionType === PurchaseOptionType.ClickAndCollect && storeId.value === null;
    if (isClickAndCollectWithoutSelectedStore && !clickAndCollectIsDisabled.value) {
        openStoreSelector();
    }
};

const isClickAndCollectAndIsDisabled = (purchaseOptionType: PurchaseOptionType): boolean => {
    const isClickAndCollect = purchaseOptionType === PurchaseOptionType.ClickAndCollect;
    if (isClickAndCollect && storeInventoryData.value) {
        return clickAndCollectIsDisabled.value;
    }
    return false;
};

const clickAndCollectIsDisabled = computed(() => {
    if (storeInventoryData.value) {
        const hasBackorder = clickAndCollectOption.value.backOrderAvailable;
        const isInStockInSelectedStore = storeId.value && storeInventoryData.value.some((store) => store?.storeId === storeId.value && store?.status.namedParams?.storeInventoryCount > 0);
        const isInStockInSomeStore = storeInventoryData.value.some((store) => store?.status.namedParams?.storeInventoryCount > 0);
        const isInStock = storeId.value ? isInStockInSelectedStore : isInStockInSomeStore;
        return hasBackorder ? false : !isInStock;
    }
    return false;
});


return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass({'hidden': _unref(hidePurchaseOptions)}),
    "aria-hidden": _unref(hidePurchaseOptions)
  }, [
    (_unref(showRequestDelivery))
      ? (_openBlock(), _createBlock(ProductDetailRequestDelivery, { key: 0 }))
      : (_openBlock(), _createElementBlock("fieldset", _hoisted_2, [
          _createElementVNode("legend", _hoisted_3, _toDisplayString(_unref(labels).purchaseOptionLegend), 1),
          _createVNode(_TransitionGroup, { name: "o-anim-fade-300" }, {
            default: _withCtx(() => [
              (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_unref(productData).purchasePossibilities, (purchaseOption) => {
                return (_openBlock(), _createElementBlock("div", {
                  key: purchaseOption.type,
                  class: "mb-x3 md:mb-x2 last:mb-0"
                }, [
                  _createElementVNode("div", {
                    class: _normalizeClass({ 'flex flex-wrap items-baseline gap-x1': purchaseOption.type === _unref(PurchaseOptionType).ClickAndCollect })
                  }, [
                    _createVNode(InputRadio, {
                      modelValue: _unref(selectedPurchaseOptionType),
                      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => (_isRef(selectedPurchaseOptionType) ? (selectedPurchaseOptionType).value = $event : null)),
                      disabled: isDisabled(purchaseOption.type),
                      label: _unref(labels)[purchaseOption.title],
                      value: purchaseOption.type,
                      "align-label-with-top": _unref(showMultiplePurchaseOptions),
                      "hide-label": _unref(showSinglePurchaseOption),
                      class: "font-bold",
                      name: "purchaseOptionType",
                      onChange: onPurchaseOptionChange
                    }, null, 8, ["modelValue", "disabled", "label", "value", "align-label-with-top", "hide-label"]),
                    (purchaseOption.type === _unref(PurchaseOptionType).ClickAndCollect)
                      ? (_openBlock(), _createBlock(ProductPurchaseOptionClickAndCollectDescription, {
                          key: 0,
                          "is-single-option": _unref(showSinglePurchaseOption),
                          "purchase-option": purchaseOption
                        }, null, 8, ["is-single-option", "purchase-option"]))
                      : (purchaseOption.description)
                        ? (_openBlock(), _createElementBlock("span", {
                            key: 1,
                            class: _normalizeClass({'ml-30 inline-block': _unref(showMultiplePurchaseOptions)})
                          }, _toDisplayString(_unref(labels)[purchaseOption.description]), 3))
                        : _createCommentVNode("", true)
                  ], 2)
                ]))
              }), 128))
            ]),
            _: 1
          })
        ]))
  ], 10, _hoisted_1))
}
}

})